/** Ergonomic wrappers over the sync engine for components. */
import { sync, uuid } from "./sync";
import type { BatchResult, EntityMap, EntityName, Id, Op } from "./types";

type Data<E extends EntityName> = Partial<EntityMap[E]> & Record<string, unknown>;

export function create<E extends EntityName>(entity: E, data: Data<E>, id: Id = uuid()): { id: Id; done: Promise<BatchResult> } {
  const done = sync.mutate([{ op: "create", entity, id, data }]);
  done.catch(() => {});
  return { id, done };
}

export function update<E extends EntityName>(entity: E, id: Id, data: Data<E>): Promise<BatchResult> {
  const p = sync.mutate([{ op: "update", entity, id, data }]);
  p.catch(() => {});
  return p;
}

export function updateMany<E extends EntityName>(entity: E, ids: Id[], data: Data<E>): Promise<BatchResult> {
  const p = sync.mutate(ids.map((id) => ({ op: "update", entity, id, data }) as Op));
  p.catch(() => {});
  return p;
}

export function addTo<E extends EntityName>(entity: E, id: Id, field: string, values: Id[]) {
  return sync.mutate([{ op: "add", entity, id, field, values }]).catch(() => undefined);
}

export function removeFrom<E extends EntityName>(entity: E, id: Id, field: string, values: Id[]) {
  return sync.mutate([{ op: "remove", entity, id, field, values }]).catch(() => undefined);
}

export function archive(entity: EntityName, id: Id) {
  return sync.mutate([{ op: "archive", entity, id }]).catch(() => undefined);
}

export function unarchive(entity: EntityName, id: Id) {
  return sync.mutate([{ op: "unarchive", entity, id }]).catch(() => undefined);
}

export function remove(entity: EntityName, id: Id) {
  return sync.mutate([{ op: "delete", entity, id }]).catch(() => undefined);
}

/** Server-side multi-step action. Rejects with the server's message on failure. */
export function action<T = unknown>(name: string, args: Record<string, unknown>): Promise<T> {
  return sync.mutate([{ op: "action", name, args }]).then((r) => r.results[0] as T);
}

export function batch(ops: Op[]) {
  return sync.mutate(ops);
}
